import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import {
  loginUser,
  logoutUser,
  isAuthenticated as checkIsAuthenticated,
  getStoredUser,
  refreshToken,
  startTokenCleanup,
  cleanupExpiredTokens
} from '../services/authService';
import {
  getFromStorage,
  removeFromStorage
} from '../utils/storage';
import { REFRESH_TOKEN_STORAGE_KEY, TOKEN_STORAGE_KEY } from '../utils/constants';

// Criar o contexto de autenticação
const AuthContext = createContext();

/**
 * Provider de autenticação para gerenciar o estado de autenticação do usuário
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Inicialização do contexto - verifica se o usuário está autenticado
  useEffect(() => {
    let stopCleanup = null;
    
    const checkAuth = async () => {
      setIsLoading(true);

      try {
        // Limpar tokens expirados ANTES de verificar autenticação
        cleanupExpiredTokens();

        const isUserAuthenticated = checkIsAuthenticated();

        if (isUserAuthenticated) {
          const storedUser = getStoredUser();

          // Token válido mas dados do usuário ausentes ou corrompidos no storage:
          // trata como não autenticado para forçar novo login.
          if (!storedUser?.username) {
            console.warn('[AUTH] Token válido mas dados do usuário ausentes no storage — forçando logout');
            setUser(null);
            setIsAuthenticated(false);
          } else {
            // Se o access token está ausente mas o refresh é válido, renova proativamente
            // para evitar que a primeira chamada de API retorne 401 desnecessariamente.
            const accessToken = getFromStorage(TOKEN_STORAGE_KEY, false);
            if (!accessToken) {
              console.log('🔄 [AUTH] Access token ausente — renovando proativamente...');
              try {
                const refreshTokenValue = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
                await refreshToken(refreshTokenValue);
                console.log('🔄 [AUTH] Access token renovado com sucesso no checkAuth');
              } catch (refreshErr) {
                console.warn('[AUTH] Renovação proativa falhou — usuário será redirecionado ao login:', refreshErr.message);
                setUser(null);
                setIsAuthenticated(false);
                return;
              }
            }

            setUser(storedUser);
            setIsAuthenticated(true);

            // ✅ CORREÇÃO: Iniciar limpeza automática APENAS se usuário autenticado
            stopCleanup = startTokenCleanup();
            console.log('🧹 [AUTH] Sistema de limpeza iniciado para usuário autenticado');
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          console.log('🧹 [AUTH] Usuário não autenticado - limpeza automática não iniciada');
        }
      } catch (err) {
        console.error('Error checking authentication:', err);
        setError('Error checking authentication');
      } finally {
        setIsLoading(false);
      }
    };
    
    // Executar verificação
    checkAuth();
    
    // Ouvir evento de logout forçado (por exemplo, token expirado)
    const handleForceLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
      
      // ✅ CORREÇÃO: Parar limpeza automática quando usuário fizer logout
      if (typeof stopCleanup === 'function') {
        stopCleanup();
        console.log('🧹 [AUTH] Sistema de limpeza parado devido a logout forçado');
      }
    };
    
    window.addEventListener('auth:logout', handleForceLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleForceLogout);
      
      // Parar limpeza automática quando componente for desmontado
      if (typeof stopCleanup === 'function') {
        stopCleanup();
        console.log('🧹 [AUTH] Sistema de limpeza automática parado na desmontagem');
      }
    };
  }, []);
  
  // Função para login
  const login = useCallback(async (username, password) => {
    console.log('🔐 [AUTH_CONTEXT] Iniciando login para:', username);
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await loginUser(username, password);
      
      console.log('🔐 [AUTH_CONTEXT] Login bem-sucedido, atualizando estado');
      
      // Definir usuário e estado de autenticação
      setUser({ username });
      setIsAuthenticated(true);
      
      return response;
    } catch (err) {
      const errorMessage = err.message || 'Error during login';
      console.error('🔐 [AUTH_CONTEXT] Erro capturado:', errorMessage);
      setError(errorMessage);
      
      // Re-throw the error so LoginPage can catch it
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Função para logout
  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await logoutUser();
      
      // Limpar estado de autenticação
      setUser(null);
      setIsAuthenticated(false);
      
      return true;
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message || 'Error during logout');
      
      // Mesmo com erro, limpar o estado (o token já foi removido no serviço)
      setUser(null);
      setIsAuthenticated(false);
      
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Função para atualizar o token
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const refreshTokenValue = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
      
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }
      
      const response = await refreshToken(refreshTokenValue);
      return response;
    } catch (err) {
      console.error('Token refresh error:', err);
      setError(err.message || 'Error refreshing token');
      
      // Em caso de erro na atualização do token, fazer logout
      setUser(null);
      setIsAuthenticated(false);
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Valor do contexto
  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refresh,
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook para usar o contexto de autenticação
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext; 